import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FlexStack, Icon, Text } from '~/components/atomic';
import { IconButtonWrapper } from '~/components/common';
import CreatorTag from '~/components/common/CharacterCard/CreatorTag/CreatorTag';
import { GetCommentData } from '~/types';
import { parseLikeNumber } from '~/utils';
import { convertToLocalRelativeTime } from '~/utils/dayjs';
import { MINUTE } from '~/constants';

interface CommentProps {
  data: GetCommentData;
  isSubMenuOpen: boolean;
  toggleSubMenu: () => void;
  handleDeleteComment: () => void;
  handleEditComment: () => void;
  handleCommentLikeClick: (comment_id: number, is_liked: boolean) => void;
  setOpenCommentId: React.Dispatch<React.SetStateAction<number | null>>;
}

export default function Comment({
  data: {
    comment_id,
    writer,
    created_time,
    content,
    is_liked,
    like_count,
    is_writer,
  },
  isSubMenuOpen,
  toggleSubMenu,
  handleDeleteComment,
  handleEditComment,
  handleCommentLikeClick,
  setOpenCommentId,
}: CommentProps) {
  const subMenuRef = useRef<HTMLDivElement>(null);
  const [relativeTime, setRelativeTime] = useState<string>(
    convertToLocalRelativeTime(created_time) || '',
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRelativeTime(convertToLocalRelativeTime(created_time) || '');
    }, MINUTE);

    return () => clearInterval(intervalId);
  }, [created_time]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        subMenuRef.current &&
        !subMenuRef.current.contains(event.target as Node)
      ) {
        setOpenCommentId(null);
      }
    }

    if (isSubMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSubMenuOpen, toggleSubMenu]);

  return (
    <>
      <ContentWrapper key={comment_id}>
        <FlexStack>
          <FlexStack
            direction='row'
            spacing={4}
            css={css`
              align-items: center;
            `}
          >
            <CreatorTag create_username={writer} isNameFull />
            <Text fontStyle='text.caption_medium' color='content_light_3'>
              {relativeTime}
            </Text>
            <div
              css={css`
                position: relative;
              `}
            >
              {is_writer ? (
                <IconButtonWrapper
                  size={24}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSubMenu();
                  }}
                >
                  <Icon iconName='Edit' size={16} />
                </IconButtonWrapper>
              ) : null}

              {isSubMenuOpen && (
                <SubMenu className='sub_menu' ref={subMenuRef}>
                  <button onClick={handleEditComment}>
                    <Text
                      as={'span'}
                      color='content_light_1'
                      fontStyle='text.body16_medium'
                    >
                      Edit
                    </Text>
                  </button>
                  <button onClick={handleDeleteComment}>
                    <Text
                      as={'span'}
                      color='content_light_1'
                      fontStyle='text.body16_medium'
                    >
                      Delete
                    </Text>
                  </button>
                </SubMenu>
              )}
            </div>
          </FlexStack>

          <div>{content}</div>
        </FlexStack>
        <CommentLikeButtonWrapper
          isLiked={is_liked}
          onClick={() => handleCommentLikeClick(comment_id, is_liked)}
        >
          <Icon iconName='ThumbsUp' />
          <Text fontStyle='text.caption_medium'>
            {parseLikeNumber(like_count)}
          </Text>
        </CommentLikeButtonWrapper>
      </ContentWrapper>
    </>
  );
}

export const ContentWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
`;

export const CommentLikeButtonWrapper = styled(IconButtonWrapper)<{
  isLiked: boolean;
}>`
  ${({ theme, isLiked }) => css`
    flex-direction: column;
    flex-shrink: 0;
    color: ${isLiked ? theme.themeColor.main_1 : 'inherit'};
    gap: 4px;
  `}
`;

export const SubMenu = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: absolute;
    top: 8px;
    left: 24px;
    flex-direction: column;
    padding: 8px;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 8px;
    background: ${theme.backgroundColor.bg_dark_2};
    gap: 4px;

    & > button {
      display: block;
      width: 110px;
      height: 30px;
      padding: 4px;
      border-radius: 4px;
      text-align: left;

      &:hover {
        background: ${theme.backgroundColor.bg_dark_3};
      }
    }
  `}
`;
